import { Brand, OnlineStore, PostalAddress, SearchAction } from "schema-dts"
import { routes } from "src/config/routes"
import { AggregatedReviewData } from "src/data/product-details/types"
import { META_DATA } from "src/metadata/constants"
import { createLdJsonSchemaId } from "src/seo/linkedDataHelpers"
import { createRatingLd } from "src/seo/ratingLd"

const addressLd: PostalAddress = {
  // https://schema.org/PostalAddress
  "@type": "PostalAddress",
  "@id": createLdJsonSchemaId("PostalAddress"),
  streetAddress: META_DATA.address.street,
  postalCode: META_DATA.address.postalCode,
  addressLocality: META_DATA.address.town,
  addressCountry: META_DATA.address.countryCode,
}

// https://developers.google.com/search/docs/advanced/structured-data/sitelinks-searchbox
const searchActionLd: SearchAction = {
  // https://schema.org/SearchAction
  "@type": "SearchAction",
  "@id": createLdJsonSchemaId("SearchAction"),
  target: {
    // https://schema.org/EntryPoint
    "@type": "EntryPoint",
    "@id": createLdJsonSchemaId("EntryPoint"),
    urlTemplate: META_DATA.origin + routes.search + "?q={search_term_string}",
  },
  // @ts-expect-error
  "query-input": "required name=search_term_string",
}

export const brandLd: Brand = {
  // https://schema.org/Brand
  "@type": "Brand",
  "@id": createLdJsonSchemaId("Brand"),
  name: META_DATA.brandName,
  logo: META_DATA.logo,
}

export const baseStoreLd = {
  // https://schema.org/OnlineStore
  "@type": "OnlineStore",
  "@id": createLdJsonSchemaId("OnlineStore"),
  url: META_DATA.origin,
  sameAs: Object.values(META_DATA.socials),
  logo: META_DATA.logo,
  name: META_DATA.brandName,
  legalName: META_DATA.address.companyName,
  description: META_DATA.slogan,
  address: addressLd,
  vatID: META_DATA.vatId,
  potentialAction: searchActionLd,
  brand: brandLd,
} satisfies OnlineStore

// https://developers.google.com/search/docs/appearance/structured-data/organization
export function createStoreLd(rating: AggregatedReviewData, allowedCountryCodes: string[]) {
  const ld: OnlineStore = {
    // https://schema.org/OnlineStore
    ...baseStoreLd,
    aggregateRating: createRatingLd("OnlineStore", rating),
    hasMerchantReturnPolicy: {
      "@type": "MerchantReturnPolicy",
      applicableCountry: allowedCountryCodes.sort().map((code) => code.toLowerCase()),
      merchantReturnDays: 14,
      refundType: "https://schema.org/FullRefund",
      returnFees: "https://schema.org/ReturnFeesCustomerResponsibility",
      returnMethod: "https://schema.org/ReturnByMail",
      returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
      returnPolicyCountry: META_DATA.address.countryCode,
    },
  }

  return ld
}
